import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Typography,
  Box,
  Button,
  IconButton,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles(theme => ({
  carousel: {
    position: 'relative',
    borderRadius: '12px',
    marginBottom: theme.spacing(4),
    minHeight: '300px',
    overflow: 'hidden',
  },
  slide: {
    position: 'relative',
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(5, 8),
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    pointerEvents: 'none',
    zIndex: 0,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(to right, rgba(21, 25, 40, 0.92) 40%, rgba(21, 25, 40, 0.4) 100%)',
    borderRadius: '12px',
    zIndex: 1,
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '600px',
  },
  title: {
    color: '#FFFFFF',
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: theme.spacing(2),
  },
  description: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '16px',
    lineHeight: 1.6,
    marginBottom: theme.spacing(3),
  },
  ctaButton: {
    backgroundColor: '#F67035',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 700,
    borderRadius: '12px',
    padding: theme.spacing(1.2, 3),
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#E65F2A',
    },
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  navLeft: {
    left: theme.spacing(1),
  },
  navRight: {
    right: theme.spacing(1),
  },
  dots: {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: theme.spacing(1),
    zIndex: 3,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    border: 'none',
    padding: 0,
  },
  dotActive: {
    backgroundColor: '#F67035',
  },
}));

interface Slide {
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
  backgroundImage: string;
}

interface HeroCarouselProps {
  slides: Slide[];
  autoRotateMs?: number;
}

export const HeroCarousel: React.FC<HeroCarouselProps> = ({
  slides,
  autoRotateMs = 6000,
}) => {
  const classes = useStyles();
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setCurrent(p => (p - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(next, autoRotateMs);
    return () => clearInterval(timer);
  }, [next, autoRotateMs]);

  const slide = slides[current];

  return (
    <Box className={classes.carousel}>
      <Box className={classes.slide}>
        <img
          src={slide.backgroundImage}
          alt={slide.title}
          className={classes.backgroundImage}
        />
        <div className={classes.gradientOverlay} />
        <div className={classes.contentWrapper}>
          <Typography className={classes.title}>{slide.title}</Typography>
          <Typography className={classes.description}>
            {slide.description}
          </Typography>
          <Button className={classes.ctaButton} href={slide.buttonHref}>
            {slide.buttonText}
          </Button>
        </div>
      </Box>

      <IconButton
        className={`${classes.navButton} ${classes.navLeft}`}
        onClick={prev}
        size="small"
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        className={`${classes.navButton} ${classes.navRight}`}
        onClick={next}
        size="small"
      >
        <ChevronRightIcon />
      </IconButton>

      <div className={classes.dots}>
        {slides.map((_, idx) => (
          <button
            key={idx}
            className={`${classes.dot} ${
              idx === current ? classes.dotActive : ''
            }`}
            onClick={() => setCurrent(idx)}
          />
        ))}
      </div>
    </Box>
  );
};
